// Field Check — "did the Recon XT walk cover every dock before we leave site?" from the raw Recon .data
// sessions and the base RINEX on CloudShare. The server finds the sessions in its CloudShare inventory; a
// separate worker streams them read-only, works out the trajectory (PPK + IMU) and lidar coverage, and
// writes the results to the project's S3 folder (services/ais-api/src/field-check.js,
// scripts/recon/field_check_worker.py). Admin-only; runs are started by hand per marina.

const API = "/api/field-check";
// Recon units with a calibration on the worker (scripts/recon/units/<SERIAL>.json). Provisional = derived
// from the unit's own log, not yet checked against a reference cloud.
const UNITS = { F8C75C: "calibrated", A84756: "calibrated" };  // A84756 validated for coverage 2026-10-17 (units/A84756.json)
const POLL_MS = 10_000;

function esc(value) {
  return String(value ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}
const fmtGB = (n) => `${((Number(n) || 0) / 1e9).toFixed(1)} GB`;
const fmtWhen = (iso) => { const d = new Date(iso); return iso && Number.isFinite(d.getTime()) ? d.toLocaleString() : "—"; };
const fmtSessionTime = (iso) => { const d = new Date(iso); return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 16).replace("T", " ") + " UTC" : iso; };
const fmtDur = (s) => (Number.isFinite(Number(s)) ? `${Math.round(Number(s) / 60)} min` : "—");
const statusColor = { done: "#22c55e", running: "#38bdf8", queued: "var(--muted)", failed: "#ef4444", skipped: "#f59e0b", cancelled: "var(--muted)" };

function createRuntime(app) {
  let containerEl = null;
  let data = null;
  let loading = false;
  let error = "";
  let busy = false;
  let pollTimer = null;
  let loadedFor = "";

  const project = () => app?.services?.context?.getActiveProject?.() || null;
  const activeRun = () => (data?.runs || []).find((r) => r.status === "queued" || r.status === "running") || null;
  const userName = () => app?.auth?.user?.email || app?.auth?.email || app?.auth?.username || null;

  async function load() {
    const p = project();
    if (!p) { data = null; render(); return; }
    loading = true; error = ""; loadedFor = p.code; render();
    try {
      const res = await fetch(`${API}/${encodeURIComponent(p.code)}`);
      const j = await res.json();
      if (!res.ok || !j.ok) throw new Error(j.error || `HTTP ${res.status}`);
      if (project()?.code === p.code) data = j;
    } catch (e) { error = String(e?.message || e); }
    loading = false; render(); schedulePoll();
  }

  function schedulePoll() {
    clearTimeout(pollTimer);
    if (containerEl && activeRun()) pollTimer = setTimeout(load, POLL_MS);
  }

  async function startRun() {
    const p = project(); if (!p || busy) return;
    const n = data?.discovery?.sessions?.length || 0;
    if (!window.confirm(`Run the field check on all ${n} Recon session${n === 1 ? "" : "s"} for ${p.code}? Data is streamed read-only from CloudShare; nothing there is changed.`)) return;
    busy = true; render();
    try {
      const res = await fetch(`${API}/${encodeURIComponent(p.code)}/run`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ by: userName() }) });
      const j = await res.json();
      if (!res.ok || !j.ok) throw new Error(j.error || `HTTP ${res.status}`);
    } catch (e) { error = String(e?.message || e); }
    busy = false; await load();
  }

  async function cancelRun(id) {
    const p = project(); if (!p) return;
    if (!window.confirm("Cancel this field check? Sessions already processed keep their results.")) return;
    await fetch(`${API}/${encodeURIComponent(p.code)}/runs/${encodeURIComponent(id)}/cancel`, { method: "POST" }).catch(() => {});
    await load();
  }

  const fileUrl = (run, name, download = false) => `${API}/${encodeURIComponent(run.code)}/runs/${encodeURIComponent(run.id)}/file?name=${encodeURIComponent(name)}${download ? "&download=1" : ""}`;

  function workerHtml() {
    if (!data) return "";
    if (!data.enabled) return `<div class="small" style="color:#f59e0b;">No Field Check worker is configured on this server.</div>`;
    const seen = data.workerSeenAt ? (Date.now() - new Date(data.workerSeenAt).getTime()) / 1000 : null;
    const online = seen !== null && seen < 120;
    return `<div class="small" style="color:${online ? "#22c55e" : "var(--muted)"};">Worker ${online ? `online (${esc(data.workerName || "worker")})` : data.workerSeenAt ? `last seen ${esc(fmtWhen(data.workerSeenAt))}` : "has not connected yet"}</div>`;
  }

  function discoveryHtml() {
    const d = data?.discovery; if (!d) return "";
    if (!d.sessions.length) return `<div class="small" style="color:var(--muted);">No Recon sessions on CloudShare for this marina${d.marinaDirs.length ? "" : " (no CloudShare folder matched this project)"}.</div>`;
    const bases = d.bases || [];
    const rows = d.sessions.map((s) => {
      const u = UNITS[s.serial];
      const badge = u === "calibrated" ? "" : u === "provisional" ? ` <span style="color:#f59e0b;" title="Calibration derived from the unit's own log; not yet checked against a reference cloud">provisional calibration</span>` : ` <span style="color:#ef4444;">not calibrated — will be skipped</span>`;
      return `<tr><td>${esc(s.name)}</td><td>${esc(fmtSessionTime(s.startedAt))}</td><td>${esc(s.serial)}${badge}</td><td style="text-align:right;">${s.parts}</td><td style="text-align:right;">${esc(fmtGB(s.bytes))}</td>
        <td>${s.unstable ? `<span style="color:#f59e0b;">still uploading</span>` : s.hasServiceLog ? "" : `<span style="color:#f59e0b;">no service.log</span>`}</td></tr>`;
    }).join("");
    return `
      <table class="small" style="width:100%; border-collapse:collapse;">
        <thead><tr style="color:var(--muted); text-align:left;"><th>Session</th><th>Started</th><th>Unit</th><th style="text-align:right;">Parts</th><th style="text-align:right;">Size</th><th></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="small" style="color:var(--muted); margin-top:6px;">Base files: ${bases.length ? bases.map((b) => `${esc(b.name)}${b.rinex ? "" : ` <span style="color:#f59e0b;">(raw Trimble — export to RINEX .26O)</span>`}`).join(", ") : `<span style="color:#ef4444;">none found</span>`}</div>`;
  }

  function checksHtml(checks) {
    if (!Array.isArray(checks) || !checks.length) return "";
    // { name, ok, detail } (recon_coverage.py); older results were [name, ok, detail]
    const norm = checks.map((c) => (Array.isArray(c) ? { name: c[0], ok: c[1], detail: c[2] } : c));
    const bad = norm.filter((c) => !c.ok);
    return bad.length ? bad.map((c) => `<div style="color:#ef4444;">✗ ${esc(c.name)}: ${esc(c.detail)}</div>`).join("") : `<div style="color:#22c55e;">✓ ${norm.length} completeness checks passed</div>`;
  }

  function sessionRowHtml(run, s) {
    const files = Array.isArray(s.files) && s.files.length;
    const extra = s.status === "done" ? [
      s.ppk ? `PPK ${s.ppk.fixedPct}% fixed` : "",
      s.baseSource ? `base: ${esc(s.baseSource === "cors" ? "NOAA CORS" : s.baseSource === "opus" ? "OPUS" : s.baseSource)}` : "",
      s.boundaryCoveredPct != null ? `${s.boundaryCoveredPct}% of boundary` : "",
      s.durationS ? fmtDur(s.durationS) + " walk" : "",
      s.unitValidated === false ? `<span style="color:#f59e0b;">provisional calibration</span>` : "",
    ].filter(Boolean).join(" · ") : "";
    return `
      <div style="padding:6px 0; border-top:1px solid rgba(127,127,127,0.2);">
        <div><b>${esc(s.name)}</b> <span style="color:${statusColor[s.status] || "inherit"};">${esc(s.status)}</span>${s.message && s.status !== "done" ? ` — ${esc(s.message)}` : ""}</div>
        ${extra ? `<div style="color:var(--muted);">${extra}</div>` : ""}
        ${s.status === "done" ? checksHtml(s.checks) : ""}
        ${files ? `<div><a href="${fileUrl(run, `${s.name}/coverage.png`)}" target="_blank" rel="noopener">coverage map</a> · <a href="${fileUrl(run, `${s.name}/session.json`, true)}">details (JSON)</a></div>` : ""}
      </div>`;
  }

  function runHtml(run) {
    if (!run) return `<div class="small" style="color:var(--muted);">No field check has been run for this marina yet.</div>`;
    const active = run.status === "queued" || run.status === "running";
    const sum = run.summary || {};
    const visit = sum.files?.includes?.("coverage.png");
    return `
      <div class="small">
        <div><span style="color:${statusColor[run.status] || "inherit"}; font-weight:600;">${esc(run.status.toUpperCase())}</span> · requested ${esc(fmtWhen(run.requestedAt))}${run.requestedBy ? ` by ${esc(run.requestedBy)}` : ""}</div>
        <div style="color:var(--muted);">${esc(run.message || "")}</div>
        ${active ? `<progress max="100" value="${Number(run.progress) || 0}" style="width:100%;"></progress> <button class="btn small" data-cancel-run="${esc(run.id)}">Cancel</button>` : ""}
        ${visit ? `
          <div style="margin-top:8px; padding:8px; border:1px solid rgba(127,127,127,0.25); border-radius:6px;">
            <div style="font-weight:600;">Visit coverage (all sessions together)</div>
            <div>${sum.docksTotal ? `<b>${sum.docksCovered} of ${sum.docksTotal}</b> slips covered by lidar` : "No slips drawn yet — scored against the project boundary"}${sum.boundaryCoveredPct != null ? ` · <b>${sum.boundaryCoveredPct}%</b> of the boundary` : ""}</div>
            ${Array.isArray(sum.docksMissed) && sum.docksMissed.length ? `<div style="color:#ef4444;">Missed: ${sum.docksMissed.map(esc).join(", ")}</div>` : ""}
            <a href="${fileUrl(run, "coverage.png")}" target="_blank" rel="noopener"><img src="${fileUrl(run, "coverage.png")}" alt="Visit coverage map" style="width:100%; margin-top:6px; border-radius:4px; background:#0b1220;" /></a>
            <div><a href="${fileUrl(run, "coverage.png", true)}">Download map (PNG)</a> · <a href="${fileUrl(run, "coverage.json", true)}">Download results (JSON)</a></div>
          </div>` : ""}
        <div style="margin-top:6px;">${(run.sessions || []).map((s) => sessionRowHtml(run, s)).join("")}</div>
      </div>`;
  }

  function historyHtml() {
    const older = (data?.runs || []).slice(1);
    if (!older.length) return `<div class="small" style="color:var(--muted);">No earlier runs.</div>`;
    return older.map((r) => `<div class="small"><span style="color:${statusColor[r.status] || "inherit"};">${esc(r.status)}</span> · ${esc(fmtWhen(r.requestedAt))} — ${esc(r.message || "")}${r.summary?.files?.includes?.("coverage.png") ? ` · <a href="${fileUrl(r, "coverage.png")}" target="_blank" rel="noopener">map</a>` : ""}</div>`).join("");
  }

  function render() {
    if (!containerEl) return;
    const p = project();
    if (!p) { containerEl.innerHTML = `<div class="small" style="color:var(--muted);">Activate a project to check its Recon field data.</div>`; return; }
    const run = data?.runs?.[0] || null;
    const canRun = data?.enabled && data?.discovery?.sessions?.length && !activeRun() && !busy;
    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted); padding-bottom:8px;">
        Checks the Recon XT sessions on CloudShare for <b style="color:inherit;">${esc(p.name || p.code)}</b> (${esc(p.code)}): file completeness, GNSS and base quality,
        and which docks the lidar actually covered — so you know the data is complete before leaving site. Read-only on CloudShare.
      </div>
      ${error ? `<div class="small" style="color:#ef4444; padding-bottom:6px;">${esc(error)}</div>` : ""}
      ${loading && !data ? `<div class="small" style="color:var(--muted);">Loading…</div>` : ""}
      <div class="subtoolset-list" style="--subtoolset-bleed-top:0px;">
        <details class="toolset-group" open>
          <summary>Sessions on CloudShare</summary>
          <div class="toolset-content">
            ${workerHtml()}
            ${discoveryHtml()}
            <div class="row" style="margin-top:8px; gap:6px;">
              <button id="fc-run" class="btn"${canRun ? "" : " disabled"}>${busy ? "Starting…" : activeRun() ? "Field check running…" : "Run field check"}</button>
              <button id="fc-refresh" class="btn small">Refresh</button>
            </div>
          </div>
        </details>
        <details class="toolset-group" open>
          <summary>Latest result</summary>
          <div class="toolset-content">${runHtml(run)}</div>
        </details>
        <details class="toolset-group">
          <summary>Earlier runs</summary>
          <div class="toolset-content">${historyHtml()}</div>
        </details>
      </div>`;
  }

  function onClick(e) {
    const t = e.target;
    if (t.closest?.("#fc-run")) { startRun(); return; }
    if (t.closest?.("#fc-refresh")) { load(); return; }
    const c = t.closest?.("[data-cancel-run]");
    if (c) cancelRun(c.getAttribute("data-cancel-run"));
  }

  function onContextChanged() { data = null; error = ""; if (containerEl) load(); }

  function mount(el) {
    if (containerEl) containerEl.removeEventListener("click", onClick);
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    app.events?.on?.("context:changed", onContextChanged);
    if (!data || loadedFor !== project()?.code) load(); else render();
  }

  function destroy() {
    clearTimeout(pollTimer);
    if (containerEl) containerEl.removeEventListener("click", onClick);
    containerEl = null;
    app.events?.off?.("context:changed", onContextChanged);
  }

  return { mount, destroy, setPanelOpen: (open) => { if (open) load(); else clearTimeout(pollTimer); } };
}

export const fieldCheckTool = {
  id: "field-check",
  name: "Field Check",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  setPanelOpen(app, open) { this._rt?.setPanelOpen?.(open); },
};
